import { useCallback, useState } from "react";
import {
  ONBOARDING_STEP,
  STEP_STATUS,
  StepId,
  StepStatus,
  StepSummary,
} from "./types";

const ALL_STEP_IDS = Object.values(ONBOARDING_STEP) as StepId[];

export type OnboardingState = {
  currentStep: StepId;
  doneStepIds: StepId[];
  isFinished: boolean;
  getStatus: (id: StepId) => StepStatus;
  getSummary: (id: StepId) => StepSummary | undefined;
  completeStep: (id: StepId, summary?: StepSummary) => void;
  goToStep: (id: StepId) => void;
  setSummary: (id: StepId, summary: StepSummary) => void;
};

export type OnboardingInit = {
  initialStep: StepId;
  initialDoneSteps: StepId[];
  steps?: StepId[];
};

export const useOnboarding = ({
  initialStep,
  initialDoneSteps,
  steps = ALL_STEP_IDS,
}: OnboardingInit): OnboardingState => {
  const lastStep = steps[steps.length - 1];

  const [currentStep, setCurrentStep] = useState<StepId>(initialStep);

  const [doneSteps, setDoneSteps] = useState<Set<StepId>>(
    () => new Set(initialDoneSteps)
  );

  const [summaries, setSummaries] = useState<Record<number, StepSummary>>({});
  const [isFinished, setIsFinished] = useState(false);

  const getStatus = useCallback(
    (id: StepId): StepStatus => {
      if (doneSteps.has(id)) return STEP_STATUS.DONE;
      if (id === currentStep && !isFinished) return STEP_STATUS.ACTIVE;
      return STEP_STATUS.LOCKED;
    },
    [doneSteps, currentStep, isFinished]
  );

  const getSummary = useCallback((id: StepId) => summaries[id], [summaries]);

  const setSummary = useCallback((id: StepId, summary: StepSummary) => {
    setSummaries((prev) => ({ ...prev, [id]: summary }));
  }, []);

  const completeStep = useCallback(
    (id: StepId, summary?: StepSummary) => {
      const nextDoneSteps = new Set(doneSteps).add(id);
      const pendingStep = steps.find((step) => !nextDoneSteps.has(step));

      if (summary !== undefined) {
        setSummaries((prev) => ({ ...prev, [id]: summary }));
      }
      setDoneSteps(nextDoneSteps);

      if (pendingStep) setCurrentStep(pendingStep);
      else if (id === lastStep) setIsFinished(true);
    },
    [doneSteps, steps, lastStep]
  );

  const goToStep = useCallback((id: StepId) => {
    setDoneSteps((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setIsFinished(false);
    setCurrentStep(id);
  }, []);

  return {
    currentStep,
    doneStepIds: steps.filter((id) => doneSteps.has(id)),
    isFinished,
    getStatus,
    getSummary,
    completeStep,
    goToStep,
    setSummary,
  };
};
